import { initComponent } from "../../utilities/common";

// Timing constants
const ICON_BASE_DELAY = 0.6;
const WORD_DELAY = 0.3;
const DESCRIPTION_BUFFER = 0.5;
const OBSERVER_THRESHOLD = 0.1;

/**
 * Get a global-like object that works in various JS environments (browser, node, workers).
 * Prefer `globalThis` when available per SonarQube recommendations, fall back to
 * environment-specific globals only when necessary for very old runtimes.
 */
const getGlobal = () => {
  // eslint-disable-next-line no-undef
  if (typeof globalThis !== "undefined") return globalThis;
  // Fallbacks for very old environments that don't support `globalThis`:
  if (typeof window !== "undefined") return window;
  if (typeof global !== "undefined") return global;
  return undefined;
};

/**
 * Apply staggered transition delays and trigger the animation for a banner.
 * Runs once per element.
 * @param {HTMLElement} container
 */
export const applyAnimationDelays = (container) => {
  // Guard: only run once per element — if it's already marked in-view, do nothing.
  if (!container || container.classList.contains("in-view")) return;

  const icon = container.querySelector('[class*="bannerIcon"]');
  const spans = container.querySelectorAll('[class*="bannerHeading"] span');
  const description = container.querySelector('[class*="bannerDescription"]');

  // Respect reduced-motion preference: make visible immediately without delays
  const _global = getGlobal();
  const prefersReducedMotion =
    _global &&
    typeof _global.matchMedia === "function" &&
    _global.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (prefersReducedMotion) {
    // Make visible immediately when reduced-motion is requested and clear any
    // inline opacity set by server-side markup so the content is visible.
    container.classList.add("in-view");
    if (icon) icon.style.opacity = "";
    spans.forEach((span) => (span.style.opacity = ""));
    if (description) description.style.opacity = "";
    return;
  }

  // Add in-view class to trigger animations (deferred to ensure transitions)

  // Base delay accounts for icon animation if icon exists
  const baseDelay = icon ? ICON_BASE_DELAY : 0;

  // Ensure icon transition starts immediately and assign its delay
  if (icon) icon.style.transitionDelay = "0s";

  // Set dynamic transition delays for each word, starting after icon
  spans.forEach((span, index) => {
    span.style.transitionDelay = `${baseDelay + index * WORD_DELAY}s`;
  });

  // Set description delay based on preceding animations
  if (description) {
    let descriptionDelay = baseDelay;
    if (spans.length > 0) {
      // Wait for all heading words plus buffer for last word to complete
      descriptionDelay =
        baseDelay + spans.length * WORD_DELAY + DESCRIPTION_BUFFER;
    }
    description.style.transitionDelay = `${descriptionDelay}s`;
  }

  // Defer adding the `in-view` class to the next animation frame so the
  // browser has time to paint the initial hidden state and transitions will run
  // correctly on first load. Before adding the class, clear any inline opacity
  // set by server-side rendering so CSS transitions can animate from 0 -> 1.
  requestAnimationFrame(() => {
    if (icon) icon.style.opacity = "";
    spans.forEach((span) => (span.style.opacity = ""));
    if (description) description.style.opacity = "";
    container.classList.add("in-view");
  });
};

/**
 * Reset inline transition delays and opacities so animation can start from hidden state.
 * @param {HTMLElement} el
 */
export const clearDelays = (el) => {
  if (!el) return;
  el.classList.remove("in-view");
  const icon = el.querySelector('[class*="bannerIcon"]');
  if (icon) icon.style.opacity = "0";
  const spans = el.querySelectorAll('[class*="bannerHeading"] span');
  spans.forEach((span) => {
    span.style.transitionDelay = "";
    span.style.opacity = "0";
  });
  const description = el.querySelector('[class*="bannerDescription"]');
  if (description) {
    description.style.transitionDelay = "";
    description.style.opacity = "0";
  }
};

/**
 * Check whether element overlaps with the viewport (simple approximation)
 * @param {HTMLElement} el
 * @returns {boolean}
 */
export const isVisible = (el) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const _global = getGlobal();

  // Compute viewport height with preference for the global innerHeight,
  // falling back to documentElement.clientHeight when available.
  let viewHeight = 0;
  if (_global && typeof _global.innerHeight === "number") {
    viewHeight = _global.innerHeight;
  } else if (typeof document !== "undefined" && document.documentElement) {
    viewHeight = document.documentElement.clientHeight;
  }
  return rect.top < viewHeight && rect.bottom > 0;
};

export const init = () => {
  initComponent("social-proof", (element) => {
    const bannerContainer = element.querySelector('[class*="bannerContainer"]');

    // Bail out if banner container is missing
    if (!bannerContainer) return;

    const hasAnimation =
      !!bannerContainer.dataset && bannerContainer.dataset.animate === "true";
    if (!hasAnimation) {
      // If animation was not requested, ensure any inline opacities
      // (set by server-side markup) are cleared so content is visible.
      const icon = bannerContainer.querySelector('[class*="bannerIcon"]');
      if (icon) icon.style.opacity = "";
      const spansReset = bannerContainer.querySelectorAll(
        '[class*="bannerHeading"] span'
      );
      spansReset.forEach((span) => (span.style.opacity = ""));
      const desc = bannerContainer.querySelector(
        '[class*="bannerDescription"]'
      );
      if (desc) desc.style.opacity = "";
      return;
    }

    clearDelays(bannerContainer);

    // Fallback for environments without IntersectionObserver (older browsers)
    if (typeof IntersectionObserver === "undefined") {
      // In environments without IntersectionObserver, trigger animation immediately.
      applyAnimationDelays(bannerContainer);
      return;
    }

    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: OBSERVER_THRESHOLD
    };

    // IntersectionObserver callbacks run asynchronously; referencing `observer`
    // inside the callback is safe because the callback will not execute until
    // after `observer` is defined.
    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          applyAnimationDelays(entry.target);
          // Unobserve after animation is triggered
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(
      observerCallback,
      observerOptions
    );

    if (isVisible(bannerContainer)) {
      applyAnimationDelays(bannerContainer);
    } else {
      observer.observe(bannerContainer);
    }
  });
};

init();
